import { useInView } from 'react-intersection-observer';
import '../styles/PrivacyPage.scss';
import { useEffect } from 'react';

function PrivacyPolicy(props: { title: string }) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  useEffect(() => {
    document.title = props.title;
  });
  return (
    <div className="privacy-page-wrapper">
      <div ref={ref} className={inView ? 'privacy slideInUp' : 'privacy'}>
        <div className="section-shadow section-shadow-from-top"></div>
        <div className="title-wrapper">
          <h1 className="future-title">
            Privacy & AI Usage Notice
            <span className="semi-transparent">Alnair Immersive Demo</span>
          </h1>
        </div>
        <div className="section-shadow section-shadow-from-bottom"></div>
        <div className="sub-text-wrapper">
          <p className="date">Last update November 28, 2025</p>
          <p>
            This notice explains how your personal data is processed and how
            artificial intelligence (AI) is used during your interaction with
            the Alnair Immersive Demo.
            <br />
            It complements SoftServe’s main{' '}
            <a
              href="https://www.softserveinc.com/en-us/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Notice
            </a>{' '}
            and applies specifically to this demo experience.
          </p>
        </div>

        <div className="text-wrapper">
          <h2 className="subtitle personal-subtitle">
            Personal Data We Collect and Use
          </h2>
          <div className="text-block">
            <h3>Data You Choose to Provide Us</h3>
            <ul>
              <li>
                <strong>Face Image:</strong> Your photo is temporarily processed
                to create a personalized avatar based on your consent (Article
                6(1)(a) GDPR).
              </li>
              <li>
                <strong>No Biometric Analysis:</strong> The demo does not scan
                or analyze facial geometry for biometric identification.
              </li>
              <li>
                <strong>Optional Publication Consent:</strong> If you agree,
                your avatar (generated from your photo) may be published on our
                website, which is done only with your consent (Article 6(1)(a)
                GDPR).
              </li>
            </ul>
          </div>
          <div className="text-block">
            <h3>Data Collected by Automated Means</h3>
            <ul>
              <li>
                <strong>Interaction Logs:</strong> We collect logs related to
                navigation and gesture-based interactions for functional and
                security purposes relying on legitimate interest (Article
                6(1)(f) GDPR) to ensure system functionality and security.
              </li>
              <li>
                <strong>Gestures:</strong> When you use the haptics device,
                gesture data is automatically collected to enable navigation and
                confirm choices during the demo experience. This data is
                functional only and does not include biometric analysis and
                processing is based on legitimate interest (Article 6(1)(f)
                GDPR) for enabling interaction and user experience.
              </li>
            </ul>
          </div>
          <div className="text-block">
            <h3>Data We Receive from Third Parties</h3>
            <p>
              We do not receive personal data from third parties for this demo.
            </p>
          </div>
          <div className="text-block">
            <h2 className="subtitle">Sharing & Disclosure</h2>
            <ul>
              <li>
                <strong>Avatar Creation:</strong> If you consent to provide your
                photo, it will be processed using Nano Banana Pro (Gemini 3 Pro
                Image) AI technology to generate your avatar. This processing
                occurs within a controlled environment and is limited to the
                purpose of avatar creation.
              </li>
              <li>
                <strong>Avatars:</strong> Avatars generated from your photo are
                never published externally without your consent.
              </li>
              <li>
                <strong>Random Metahuman Avatars:</strong> These may be used
                without additional consent.
              </li>
            </ul>
          </div>
          <div className="text-block">
            <h2 className="subtitle">Data Retention</h2>
            <ul>
              <li>
                <strong>Face Images:</strong> Deleted immediately after
                processing.
              </li>
              <li>
                <strong>Avatars:</strong> Retained only for demo purposes unless
                you opt out.
              </li>
              <li>
                <strong>Logs:</strong> Retained for 30 days based on legitimate
                interest for operational and security purposes.
              </li>
            </ul>
          </div>

          <div className="text-block">
            <h2 className="subtitle">Children’s Privacy Protection</h2>
            <p>
              This demo is intended for adults. We do not knowingly collect data
              from children under 16. If a child participates under parental
              supervision, avatars will be generated from random metahumans, not
              the child’s image. If we discover a child’s photo was provided, it
              will be deleted immediately.
            </p>
          </div>
          <div className="text-block">
            <h2 className="subtitle">AI Usage in the Demo</h2>
            <p>
              Avatars created during the demo are generated using Nano Banana
              Pro (Gemini 3 Pro Image) AI technology, which processes your photo
              solely for avatar creation. This processing is real-time and does
              not involve biometric identification, profiling, or storage beyond
              the stated retention rules.
            </p>
          </div>
          <div className="text-block">
            <h2 className="subtitle">AI Training</h2>
            <p>
              Your image will never be used for training AI models. All
              processing is performed in real time.
            </p>
          </div>
          <div className="text-block">
            <h2 className="subtitle">Additional Information</h2>
            <p>
              For all other details not covered by the Privacy & AI Usage Notice
              (e.g. privacy rights, information security, data sharing and
              disclosure practices, international transfers) please refer to
              SoftServe’s main{' '}
              <a
                href="https://www.softserveinc.com/en-us/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Notice
              </a>
              .
            </p>
          </div>
          <div className="text-blcok">
            <h2 className="subtitle">Changes to the Privacy Notice</h2>
            <p>
              We may update this notice to reflect changes in technology or
              legal requirements. Updates will be posted on this page with a
              revised “last updated” date.
            </p>
          </div>
        </div>
        <div className="contact-us-section">
          <h2 className="subtitle">Contact Us</h2>
          <p>
            <strong>
              If you have questions or concerns about this notice or data
              protection practices:
            </strong>
          </p>
          <p>
            <strong>Email:</strong>{' '}
            <a href={`mailto:privacy@softserveinc.com`}>
              privacy@softserveinc.com
            </a>
          </p>
          <p>
            <strong>Mailing Address:</strong>
          </p>
          <ul>
            <li>
              SoftServe Digital LLC (ТОВ «Софтсерв Діджітал»)
              <br /> Legal address: 81220, Lviv region, Bibrka, 17 I. Franka
              Street, apartment 4<br />
              Postal address: 79053, Lviv, 52 V. Velykohoyi Street
            </li>
            <li>
              SoftServe EU Representative: <br />
              SoftServe Poland Sp. z.o.o., 11-13 Jaworska Street, 53-612
              Wroclaw, Poland
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
export default PrivacyPolicy;
